import { GrappleModule } from '../../interfaces/api/GrappleModule';
import { LowerBracket } from '../../interfaces/api/LowerBracket';
import { MachineFixing } from '../../interfaces/api/MachineFixing';
import { Rototilt } from '../../interfaces/api/Rototilt';
import { UpperBracket } from '../../interfaces/api/UpperBracket';
import { MachineTonnage } from '../../store/interfaces/MachineTonnage';
import { ProductCategory } from '../enums/ProductCategory';
import { SwitchViewMode } from '../enums/SwitchViewMode';
import { ProductItem } from '../types/ProductItem';
import { QCIdentifier } from '../types/QCIdentifier';
import { castHelper } from './castHelper';
import { localization } from './productFilterHelper';

interface MachineFixingsProps {
  machineFixing: MachineFixing;
  machineTonnage: MachineTonnage;
  market: string;
  productCategory: ProductCategory;
  tiltrotator: Rototilt;
  activeWizardStep: number;
}

interface UpperBracketsProps {
  tiltrotator: Rototilt;
  upperBracket: UpperBracket;
}

interface LowerBracketsProps {
  tiltrotator: Rototilt;
  lowerBracket: LowerBracket;
}

interface GrappleModulesProps {
  grappleModule: GrappleModule;
  lowerBracket: LowerBracket;
  machineTonnage: MachineTonnage;
  market: string;
  tiltrotator: Rototilt;
}

interface ToolProductItemProps {
  machineTonnage: MachineTonnage;
  market: string;
  productItem: ProductItem;
  selectedLowerBracket: LowerBracket;
  selectedTiltrotator: Rototilt;
}

interface MinMaxMachineWeightProps {
  minMachineWeight: number;
  maxMachineWeight: number;
}

const getMinMaxMachineWeight = (
  machineTonnage: MachineTonnage,
  market: string
): MinMaxMachineWeightProps => {
  if (machineTonnage.switchViewMode === SwitchViewMode.Tonnage) {
    return {
      minMachineWeight: castHelper.toKilogram(machineTonnage.tonnage),
      maxMachineWeight: castHelper.toKilogram(machineTonnage.tonnage),
    };
  }

  return {
    minMachineWeight: localization.value(machineTonnage.machineModel.minMachineWeight, market),
    maxMachineWeight: localization.value(machineTonnage.machineModel.maxMachineWeight, market),
  };
};

const machineFixings = ({
  machineFixing,
  machineTonnage,
  market,
  productCategory,
  tiltrotator,
  activeWizardStep,
}: MachineFixingsProps) => {
  const hasCompatibleControlSystem = true;
  const machineModel = machineTonnage.machineModel;

  const hasCompatibleMachineModelFixings = machineModel
    ? machineModel.compatibleMachineFixings.some((machinefixing) => {
        return machineFixing.name === machinefixing.trim();
      })
    : false;

  if (
    productCategory === ProductCategory.MachineFixings ||
    (productCategory === ProductCategory.Tools && activeWizardStep === 3)
  ) {
    if (machineTonnage.switchViewMode === SwitchViewMode.Tonnage) {
      const tonnage = machineTonnage.tonnage;
      const hasCompatibleTonnage = tonnage
        ? localization.value(machineFixing.minMachineWeight, market) <=
            castHelper.toKilogram(tonnage) &&
          castHelper.toKilogram(tonnage) <=
            localization.value(machineFixing.maxMachineWeight, market)
        : false;
      return hasCompatibleTonnage;
    }

    return hasCompatibleMachineModelFixings;
  }

  const hasCompatibleUpperBrackets = tiltrotator
    ? tiltrotator.combinations.some((combination) => {
        const compatibleUpperBrackets = localization.value(
          machineFixing.compatibleUpperBracketsPresentation,
          market
        );

        //Since the compatibleUpperBracketsPresentation is stored in a string "S60, S80" we need to convert it to an array
        const compatibleUpperBracketsList = compatibleUpperBrackets.split(',');

        return compatibleUpperBracketsList.some((upperbracket) => {
          return combination.upperBracket === upperbracket.trim();
        });
      })
    : false;

  if (machineModel && !tiltrotator) {
    return hasCompatibleControlSystem && hasCompatibleMachineModelFixings;
  }

  if (tiltrotator && !machineModel) {
    return hasCompatibleControlSystem && hasCompatibleUpperBrackets;
  }

  if (machineModel && tiltrotator) {
    return (
      hasCompatibleControlSystem && hasCompatibleMachineModelFixings && hasCompatibleUpperBrackets
    );
  }
  return hasCompatibleControlSystem && machineModel;
};

const upperBrackets = ({ tiltrotator, upperBracket }: UpperBracketsProps) => {
  const hasCompatibleControlSystem = true;

  if (tiltrotator) {
    const hasCombinations = tiltrotator.combinations.some((combination) => {
      return hasCompatibleControlSystem && combination.upperBracket === upperBracket.name;
    });

    if (hasCombinations && hasCompatibleControlSystem) {
      return hasCompatibleControlSystem && upperBracket;
    }

    return false;
  }

  return hasCompatibleControlSystem && upperBracket;
};

const lowerBrackets = ({ lowerBracket, tiltrotator }: LowerBracketsProps) => {
  const hasCompatibleControlSystem = true;

  if (tiltrotator) {
    const hasCombinations = tiltrotator.combinations.some((combination) => {
      return hasCompatibleControlSystem && combination.lowerBracket === lowerBracket.name;
    });

    if (hasCombinations && hasCompatibleControlSystem) {
      return hasCompatibleControlSystem && lowerBracket;
    }

    return false;
  }

  return hasCompatibleControlSystem && lowerBracket;
};

const grappleModules = ({
  grappleModule,
  lowerBracket,
  machineTonnage,
  market,
  tiltrotator,
}: GrappleModulesProps) => {
  const compatible = (minMachineWeight: number, maxMachineWeight: number) => {
    const buckmins = localization.value(grappleModule.minMachineWeight, market);
    const buckmaxs = localization.value(grappleModule.maxMachineWeight, market);
    const aa = minMachineWeight + maxMachineWeight;
    const bb = maxMachineWeight - minMachineWeight;
    const cc = aa - bb;
    const dd = buckmins + buckmaxs;
    const ee = buckmaxs - buckmins;
    const ff = dd + ee;
    return ff >= cc && maxMachineWeight > buckmins;
  };

  const hasCompatibleRototilt = tiltrotator
    ? grappleModule.compatibleRototiltNames &&
      grappleModule.compatibleRototiltNames.some((tiltName) => {
        return tiltName === tiltrotator.name;
      })
    : false;

  const { minMachineWeight, maxMachineWeight } = getMinMaxMachineWeight(machineTonnage, market);
  const hasWeightRange = compatible(minMachineWeight, maxMachineWeight);

  const hasCompatibleLowerBrackets = lowerBracket
    ? grappleModule.compatibleLowerBrackets &&
      grappleModule.compatibleLowerBrackets.some((lowerbracket) => {
        return lowerbracket === lowerBracket.name;
      })
    : false;

  if (machineTonnage.switchViewMode === SwitchViewMode.Tonnage && !tiltrotator) {
    return hasWeightRange;
  }

  if (
    !tiltrotator &&
    machineTonnage.switchViewMode === SwitchViewMode.MachineSearch &&
    lowerBracket
  ) {
    return hasWeightRange && hasCompatibleLowerBrackets;
  }

  if (tiltrotator && lowerBracket) {
    return hasCompatibleRototilt && hasCompatibleLowerBrackets;
  }

  if (tiltrotator) {
    return hasCompatibleRototilt;
  }

  if (lowerBracket) {
    return hasCompatibleLowerBrackets;
  }

  if (machineTonnage.switchViewMode === SwitchViewMode.MachineSearch) {
    return hasWeightRange;
  }

  return true;
};

const toolsProductItems = ({
  machineTonnage,
  market,
  productItem,
  selectedLowerBracket,
  selectedTiltrotator,
}: ToolProductItemProps) => {
  const compatibleRototiltNames: string[] = getRototiltNames(productItem.compatibleRototiltNames);
  const { minMachineWeight, maxMachineWeight } = getMinMaxMachineWeight(machineTonnage, market);

  const compatible = (minMachineWeight: number, maxMachineWeight: number) => {
    const buckmins = localization.value(productItem.minMachineWeight, market);
    const buckmaxs = localization.value(productItem.maxMachineWeight, market);
    const aa = minMachineWeight + maxMachineWeight;
    const bb = maxMachineWeight - minMachineWeight;
    const cc = aa - bb;
    const dd = buckmins + buckmaxs;
    const ee = buckmaxs - buckmins;
    const ff = dd + ee;
    return ff >= cc && maxMachineWeight >= buckmins;
  };

  const hasWeightRange = compatible(minMachineWeight, maxMachineWeight);
  const hasCompatibleLowerBrackets = selectedLowerBracket
    ? productItem.compatibleLowerBrackets &&
      productItem.compatibleLowerBrackets.some((lowerbracket) => {
        return lowerbracket === selectedLowerBracket.name;
      })
    : false;

  const hasCompatibleRototilt = selectedTiltrotator
    ? compatibleRototiltNames &&
      compatibleRototiltNames.some((tiltName) => {
        return tiltName === selectedTiltrotator.name;
      })
    : false;

  // Old Logic: Case 1
  if (machineTonnage.switchViewMode === SwitchViewMode.Tonnage && !selectedTiltrotator) {
    return hasWeightRange;
  }

  // Old Logic: Case 2
  if (
    machineTonnage.switchViewMode === SwitchViewMode.MachineSearch &&
    !selectedTiltrotator &&
    selectedLowerBracket
  ) {
    return hasWeightRange && hasCompatibleLowerBrackets;
  }

  // Old Logic: Case 3
  if (selectedTiltrotator && selectedLowerBracket) {
    return hasCompatibleRototilt && hasCompatibleLowerBrackets;
  }

  // Old Logic: Case 4
  if (selectedTiltrotator) {
    return hasCompatibleRototilt;
  }

  // Old Logic: Case 5
  if (selectedLowerBracket) {
    return hasCompatibleLowerBrackets;
  }

  // Old Logic: Case 6
  if (machineTonnage.switchViewMode === SwitchViewMode.MachineSearch) {
    return hasWeightRange;
  }

  return true;
};

const getRototiltNames = (compatibleRototiltNames: string | string[]): string[] => {
  if (!compatibleRototiltNames) {
    return [];
  }

  if (Array.isArray(compatibleRototiltNames)) {
    return [...compatibleRototiltNames];
  }

  return [compatibleRototiltNames];
};

const filterMachineFixningsS = (machineFixing: MachineFixing) => {
  return machineFixing.name.toLowerCase().indexOf(QCIdentifier) === -1;
};

const filterMachineFixningsQC = (machineFixing: MachineFixing) => {
  return machineFixing.name.toLowerCase().indexOf(QCIdentifier) > -1;
};

export const filterHelper = {
  filterMachineFixningsS,
  filterMachineFixningsQC,
  grappleModules,
  lowerBrackets,
  machineFixings,
  toolsProductItems,
  upperBrackets,
};
